/**
 * Tool Invocation Component
 * 
 * Displays tool invocations inline with visual indicators and expandable details.
 * Shows tool status, parameters, and results with appropriate icons and animations.
 * 
 * Note: The 'respond_to_user' tool is filtered out at the parent MessageList level
 * as it's an internal tool that should not be visible to users.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ToolInvocation as ToolInvocationType } from './types';
import { motion, AnimatePresence } from 'framer-motion';

interface ToolInvocationProps {
  invocation: ToolInvocationType;
  className?: string;
}

export function ToolInvocation({ invocation, className }: ToolInvocationProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const getToolIcon = (toolName: string): string => {
    switch (toolName) {
      case 'get_recent_posts': return '📝';
      case 'get_convergences': return '🔄';
      case 'get_brand_analytics': return '📊';
      case 'update_brand_info': return '✏️';
      default: return '🔧';
    }
  };
  
  const getToolDescription = (toolName: string): string => {
    switch (toolName) {
      case 'get_recent_posts': return 'Retrieving recent posts';
      case 'get_convergences': return 'Fetching convergences';
      case 'get_brand_analytics': return 'Loading analytics data';
      case 'update_brand_info': return 'Updating brand information';
      default: return `Running ${toolName}`;
    }
  };

  const getStatusIcon = () => {
    switch (invocation.status) {
      case 'pending':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
    }
  };

  const getStatusText = () => {
    switch (invocation.status) {
      case 'pending':
        return 'Running...';
      case 'success':
        return 'Complete';
      case 'error':
        return 'Failed';
      default:
        return 'Processing...';
    }
  };

  const hasResult = invocation.result && Object.keys(invocation.result).length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "bg-muted/50 rounded-lg p-3 my-2 border transition-colors",
        invocation.status === 'error' && "border-destructive/50 bg-destructive/10",
        invocation.status === 'success' && "border-green-500/20 bg-green-50/50 dark:bg-green-900/10",
        invocation.status === 'pending' && "border-blue-500/20 bg-blue-50/50 dark:bg-blue-900/10",
        className
      )}
    >
      <div 
        className="flex items-center gap-3 cursor-pointer hover:bg-muted/30 rounded p-1 -m-1 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className="text-lg" aria-label={`Tool: ${invocation.toolName}`}>
            {getToolIcon(invocation.toolName)}
          </span>
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground">
              {getToolDescription(invocation.toolName)}
            </span>
            <span className={cn(
              "text-xs px-2 py-0.5 rounded-full",
              invocation.status === 'success' && "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
              invocation.status === 'error' && "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
              invocation.status === 'pending' && "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
            )}>
              {getStatusText()}
            </span>
          </div>
          {invocation.status === 'error' && invocation.result?.error && (
            <div className="text-xs text-destructive mt-1">
              {invocation.result.error}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {hasResult && (
            <span className="text-xs text-muted-foreground">
              View details
            </span>
          )}
          {hasResult ? (
            isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
          ) : (
            <div className="w-4 h-4" /> // Spacer when no chevron
          )}
        </div>
      </div>
      
      <AnimatePresence>
        {isExpanded && hasResult && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-3 pl-9">
              <div className="space-y-3">
                {/* Parameters */}
                {invocation.parameters && Object.keys(invocation.parameters).length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-1">
                      Parameters:
                    </div>
                    <div className="text-xs font-mono bg-background rounded p-2 border">
                      <pre className="overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify(invocation.parameters, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Results */}
                {invocation.status === 'success' && invocation.result && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-1">
                      Result:
                    </div>
                    <div className="text-xs bg-background rounded p-2 border">
                      {typeof invocation.result === 'string' ? (
                        <span className="whitespace-pre-wrap">{invocation.result}</span>
                      ) : (
                        <pre className="overflow-x-auto whitespace-pre-wrap font-mono">
                          {JSON.stringify(invocation.result, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                )}

                {/* Error details */}
                {invocation.status === 'error' && invocation.result?.error && (
                  <div>
                    <div className="text-xs font-medium text-destructive mb-1">
                      Error Details:
                    </div>
                    <div className="text-xs bg-destructive/10 rounded p-2 border border-destructive/20">
                      {invocation.result.error}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}